import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { ramsService } from '../../services/ramsService';
import RamsCard from './components/RamsCard';

const RamsWorkerView = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');

  useEffect(() => {
    if (user?.id) loadAssignments();
  }, [user?.id]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await ramsService?.getRamsForWorker(user?.id);
      if (result?.error) throw result?.error;

      // Enrich with ack status
      const docs = result?.data || [];
      const enriched = await Promise.all(
        docs?.map(async (a) => {
          const ackResult = await ramsService?.getWorkerAckStatus(a?.rams_document_id, user?.id);
          if (ackResult?.error) {
            console.error('[RamsWorkerView] ack status failed:', { ramsId: a?.rams_document_id, code: ackResult?.error?.code, message: ackResult?.error?.message });
          }
          return {
            ...a,
            worker_user_id: user?.id,
            acknowledged: !!ackResult?.data,
            acknowledged_at: ackResult?.data?.acknowledged_at || null,
          };
        })
      );
      setAssignments(enriched);
    } catch (err) {
      console.error('[RamsWorkerView] loadAssignments error:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading your documents: ${err?.message}`);
      } else {
        setError(`We couldn't load your documents: ${err?.message || 'Unknown error'}. Please try again or contact your site admin.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledged = (ramsDocId) => {
    setAssignments((prev) =>
      prev?.map((a) =>
        a?.rams_document_id === ramsDocId
          ? { ...a, acknowledged: true, acknowledged_at: new Date()?.toISOString() }
          : a
      )
    );
  };

  // Unique sites from assignments
  const sites = [...new Map(
    assignments
      ?.filter((a) => a?.rams_document?.site)
      ?.map((a) => [a?.rams_document?.site?.id, a?.rams_document?.site])
  )?.values()];

  const filtered = assignments?.filter((a) => {
    const doc = a?.rams_document;
    const matchSearch =
      !searchQuery ||
      doc?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      doc?.site?.site_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'pending' && !a?.acknowledged) ||
      (filterStatus === 'confirmed' && a?.acknowledged);
    const matchSite = !filterSite || doc?.site?.id === filterSite;
    return matchSearch && matchStatus && matchSite;
  });

  const pendingCount = assignments?.filter((a) => !a?.acknowledged)?.length;
  const confirmedCount = assignments?.filter((a) => a?.acknowledged)?.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>My H&S Docs - SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-4 py-6">

            {/* Page Header */}
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name="FileCheck" size={20} className="text-accent" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">My H&S Docs</h1>
                  <p className="text-sm text-muted-foreground">Health & Safety documents assigned to you</p>
                </div>
              </div>
            </div>

            {/* Status Summary */}
            {assignments?.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-card border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={18} className="text-amber-600" />
                    <div>
                      <p className="text-xl font-bold text-foreground">{pendingCount}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </div>
                <div className="bg-card border border-success/30 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={18} className="text-success" />
                    <div>
                      <p className="text-xl font-bold text-foreground">{confirmedCount}</p>
                      <p className="text-xs text-muted-foreground">Confirmed</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending attention banner */}
            {pendingCount > 0 && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
                <Icon name="AlertTriangle" size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800 font-medium">
                  You have {pendingCount} H&S document{pendingCount !== 1 ? 's' : ''} requiring acknowledgement.
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-4 mb-4">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-destructive">Unable to load documents</p>
                  <p className="text-sm text-destructive/80 mt-0.5">{error}</p>
                  <button
                    onClick={loadAssignments}
                    className="mt-2 text-sm text-accent underline hover:no-underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            {assignments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[160px]">
                  <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                </select>
                {sites?.length > 1 && (
                  <select
                    value={filterSite}
                    onChange={(e) => setFilterSite(e?.target?.value)}
                    className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                  >
                    <option value="">All Sites</option>
                    {sites?.map((s) => (
                      <option key={s?.id} value={s?.id}>{s?.site_name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* RAMS Cards */}
            {filtered?.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <Icon name="FileCheck" size={36} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-base font-medium text-foreground">
                  {assignments?.length === 0 ? 'No H&S docs assigned yet' : 'No documents match your filters'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {assignments?.length === 0
                    ? 'Your site manager will assign H&S documents when required'
                    : 'Try adjusting your search or filters'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Pending first */}
                {filtered?.filter((a) => !a?.acknowledged)?.map((a) => (
                  <RamsCard
                    key={a?.id}
                    assignment={a}
                    onAcknowledged={handleAcknowledged}
                  />
                ))}
                {/* Then confirmed */}
                {filtered?.filter((a) => a?.acknowledged)?.map((a) => (
                  <RamsCard
                    key={a?.id}
                    assignment={a}
                    onAcknowledged={handleAcknowledged}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default RamsWorkerView;
